/**
 * AttributedSummary Component
 *
 * Displays a summary with interactive attribution to source transcript segments.
 * - Hover over text to highlight source segments in the transcript
 * - Click on text to play audio from that segment
 */

import { HeadingText } from '@/components/session/note-text/HeadingText';
import { splitHeadings } from '@/components/session/note-text/noteHeadings';
import type { HeadingPiece } from '@/components/session/note-text/noteText.types';
import { cn } from '@/lib/utils';
import type { AttributedSummaryData, SummaryPart } from '@/types/summaryGeneration';

export type { AttributedSummaryData, SourceSegment, SummaryPart } from '@/types/summaryGeneration';

interface AttributedSummaryProps {
  attributedSummary: AttributedSummaryData;
  onHighlightSegments: (segmentIds: string[] | null) => void;
  onSeekToTime: (time: number) => void;
  className?: string;
  /** Section lines (e.g. "Ástæða komu:") in semibold. */
  headings?: boolean;
}

/**
 * Renders a single attributed text part with hover and click interactions
 */
function AttributedPart({
  part,
  pieces,
  onHighlightSegments,
  onSeekToTime,
}: {
  part: SummaryPart;
  /** The part's text split into heading and body runs, when headings are shown. */
  pieces?: HeadingPiece[];
  onHighlightSegments: (segmentIds: string[] | null) => void;
  onSeekToTime: (time: number) => void;
}) {
  const content = pieces ? <HeadingText pieces={pieces} /> : part.text;
  const hasAttribution = part.segment_ids && part.segment_ids.length > 0;

  const handleMouseEnter = () => {
    if (hasAttribution) {
      onHighlightSegments(part.segment_ids);
    }
  };

  const handleMouseLeave = () => {
    onHighlightSegments(null);
  };

  const handleClick = () => {
    if (!hasAttribution) return;

    // Filter source segments to only those that are highlighted (in segment_ids)
    const segmentIdSet = new Set(part.segment_ids);
    const relevantSegments = (part.source_segments || []).filter((seg) => segmentIdSet.has(seg.segment_id));

    // Seek to the earliest of the highlighted segments
    if (relevantSegments.length > 0) {
      const earliest = relevantSegments.reduce((a, b) => (a.start < b.start ? a : b));
      onSeekToTime(earliest.start);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  // If no attribution, render as plain text
  if (!hasAttribution) {
    return <span className="text-slate-700">{content}</span>;
  }

  // Render as interactive element with keyboard accessibility
  return (
    // biome-ignore lint/a11y/useSemanticElements: Using span to preserve inline text flow within paragraph
    <span
      role="button"
      tabIndex={0}
      className={cn(
        '-mx-0.5 cursor-pointer rounded px-0.5 transition-colors duration-150',
        'hover:bg-blue-50 hover:text-blue-900',
        'active:bg-blue-100',
        'focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1'
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      title="Smelltu til að spila hljóð / Click to play audio"
    >
      {content}
    </span>
  );
}

/**
 * Generate a stable key for a summary part based on its content and segment IDs
 */
function getPartKey(part: SummaryPart, index: number): string {
  // Use segment IDs if available for stability, otherwise fall back to index with text prefix
  if (part.segment_ids && part.segment_ids.length > 0) {
    return `${part.segment_ids.join('-')}-${index}`;
  }
  // For non-attributed parts, use text hash + index
  const textPrefix = part.text.slice(0, 20).replace(/\s+/g, '_');
  return `plain-${textPrefix}-${index}`;
}

/**
 * Check if a space is needed between two parts.
 * Returns true if the current part doesn't end with whitespace and
 * the next part doesn't start with whitespace/newline.
 */
function needsSpaceBetween(currentText: string, nextText: string | undefined): boolean {
  if (!nextText) return false;

  // Don't add space if current part ends with whitespace or newline
  if (/[\s\n]$/.test(currentText)) return false;

  // Don't add space if next part starts with whitespace, newline, or punctuation
  if (/^[\s\n.,;:!?)]/.test(nextText)) return false;

  return true;
}

/**
 * Main AttributedSummary component
 */
export function AttributedSummary({
  attributedSummary,
  onHighlightSegments,
  onSeekToTime,
  className,
  headings = false,
}: AttributedSummaryProps) {
  if (!attributedSummary || !attributedSummary.parts || attributedSummary.parts.length === 0) {
    return null;
  }
  const { parts } = attributedSummary;
  const spaced = parts.map((part, index) => needsSpaceBetween(part.text, parts[index + 1]?.text));
  // Headings are found on the text as shown, joining spaces included, so offsets line up;
  // the spaces render outside the parts, so keep only each part's own pieces.
  const partPieces = headings
    ? splitHeadings(parts.flatMap((part, index) => [part.text, spaced[index] ? ' ' : ''])).filter(
        (_, index) => index % 2 === 0
      )
    : null;

  return (
    <div className={cn('whitespace-pre-wrap font-sans text-slate-700 leading-relaxed', className)}>
      {parts.map((part, index) => (
        <span key={getPartKey(part, index)}>
          <AttributedPart
            part={part}
            pieces={partPieces?.[index]}
            onHighlightSegments={onHighlightSegments}
            onSeekToTime={onSeekToTime}
          />
          {spaced[index] && ' '}
        </span>
      ))}
    </div>
  );
}

export default AttributedSummary;
